import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './Auth.css';
import logo from '../../assets/Logo - Side.png';

function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedPlan = searchParams.get('plan') || 'free';

  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    company_name: '',
    plan: preselectedPlan
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/plans`);
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      setError('All fields are required');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.first_name || !formData.last_name || !formData.company_name) {
      setError('All fields are required');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/saas/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          company_name: formData.company_name,
          plan: formData.plan
        })
      });

      const data = await res.json();

      if (res.ok) {
        // Store basic info and redirect to verification pending page
        localStorage.setItem('pendingVerification', JSON.stringify({
          email: formData.email,
          company_id: data.company?.id,
          company_name: data.company?.name
        }));
        navigate('/verify-pending');
      } else {
        setError(data.error || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = plans.find(p => p.slug === formData.plan);

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/">
            <img src={logo} alt="RentafAI" className="auth-logo" />
          </Link>
          <h1>Create Your Account</h1>
          <p>Start your 14-day free trial</p>
        </div>

        {/* Progress Steps */}
        <div className="auth-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>
            <div className="step-number">1</div>
            <span>Account</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>
            <div className="step-number">2</div>
            <span>Company</span>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <span>Plan</span>
          </div>
        </div>

        {error && <div className="auth-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Step 1: Account Info */}
          {step === 1 && (
            <div className="auth-step">
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@company.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Minimum 8 characters"
                  required
                />
              </div>
              <div className="form-group">
                <label>Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="Confirm your password"
                  required
                />
              </div>
              <button type="button" className="btn btn-primary btn-block" onClick={handleNextStep}>
                Continue
              </button>
            </div>
          )}

          {/* Step 2: Company Info */}
          {step === 2 && (
            <div className="auth-step">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="John"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  placeholder="Your Property Management Company"
                  required
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-outline" onClick={() => setStep(1)}>
                  Back
                </button>
                <button type="button" className="btn btn-primary" onClick={handleNextStep}>
                  Continue
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Plan Selection */}
          {step === 3 && (
            <div className="auth-step">
              <div className="plan-selection">
                {plans.map(plan => (
                  <div 
                    key={plan.id}
                    className={`plan-option ${formData.plan === plan.slug ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, plan: plan.slug })}
                  >
                    <div className="plan-option-header">
                      <div className="plan-option-radio">
                        {formData.plan === plan.slug && <div className="radio-dot"></div>}
                      </div>
                      <div className="plan-option-info">
                        <h4>{plan.name}</h4>
                        <p>{plan.description}</p>
                      </div>
                      <div className="plan-option-price">
                        <span className="price">${plan.price_monthly}</span>
                        <span className="period">/mo</span>
                      </div>
                    </div>
                    <div className="plan-option-limits">
                      <span>{plan.max_properties === -1 ? 'Unlimited' : plan.max_properties} properties</span>
                      <span>{plan.max_units === -1 ? 'Unlimited' : plan.max_units} units</span>
                    </div>
                  </div>
                ))}
              </div>

              {selectedPlan && (
                <div className="selected-plan-summary">
                  <p>
                    <strong>{selectedPlan.name}</strong> plan selected.
                    {selectedPlan.price_monthly > 0 
                      ? ` You'll be charged $${selectedPlan.price_monthly}/month after your 14-day trial.`
                      : ' Free forever with basic features.'}
                  </p>
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn btn-outline" onClick={() => setStep(2)}>
                  Back
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>
              </div>
            </div>
          )}
        </form>

        <div className="auth-footer">
          <p>
            Already have an account? <Link to="/">Log in</Link>
          </p>
          <p className="auth-terms">
            By creating an account, you agree to our{' '}
            <a href="#terms">Terms of Service</a> and{' '}
            <a href="#privacy">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Signup;
